import { Box, Button, Fade, Modal, Tooltip } from '@mui/material';
import styled from 'styled-components';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { GET, POST } from '../../../Commons/Utils/fetch';
import { getKoreanPhoneNumber } from '../../PrevUserSignupForm';

const FormWrapper = styled(Box).attrs({})`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 400px;
  background-color: #ffffff;
  border-radius: 0.875rem;
  padding: 2rem;
  box-sizing: border-box;
  margin: 0 auto;
`;
const FadeContainer = styled(Fade).attrs({})`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;
const Form = styled.form.attrs({})`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  gap: 1rem;
  margin-top: 1rem;
  margin-bottom: 2rem;
`;
const PhoneFieldWrapper = styled.div.attrs({
  className: 'phone-field-wrapper',
})`
  display: flex;
  column-gap: 5px;
  width: 100%;
  height: 100%;
  padding: 0;
  border: none;
  justify-content: space-between;
  item-align: center;
  display: ${(props) => (props.props?.hideAuthCodeInput ? 'none' : '')};
`;
const InputField = styled.input.attrs({})`
  width: 100%;
  height: 2rem;
  line-height: 2rem;
  padding: 0.25rem 0.5rem;
  border: 1px solid #828282;
  outline: none;
  border-radius: 0.25rem;
  vertical-align: middle;
  text-align: center;
`;
const Header = styled.strong`
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
`;
const Description = styled.p`
  font-size: 0.875rem;
  font-weight: 400;
  margin-bottom: 1rem;
`;
const HintBox = styled.div`
  width: 100%;
  padding: 0.75rem;
  border-radius: 0.375rem;
  background-color: ${(props) => (props.$strong ? '#fdecec' : '#f5f6f8')};
  border: 1px solid ${(props) => (props.$strong ? '#f5c2c2' : '#e3e5e9')};
  font-size: 0.8125rem;
  line-height: 1.5;
  color: ${(props) => (props.$strong ? '#c0392b' : '#4f4f4f')};
  text-align: center;
`;
const HintTitle = styled.strong`
  display: block;
  font-weight: 700;
  margin-bottom: 0.25rem;
`;

const LoginDialog = ({ open, onClose, setIsVerified, setTutorInfo }) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setFocus,
    setError,
    setValue,
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      phone1: '010',
    },
  });
  const validPhoneNumber = (phoneNumber) => {
    // 010으로 시작하는 11자리 숫자 여야 함
    // 숫자가 아닌 문자가 포함되면 안됨
    const validPattern = /^010[0-9]{8}$/g;
    const isValid = validPattern.test(phoneNumber);
    return !isValid;
  };

  const [requestedAuthCode, setRequestedAuthCode] = useState(false);
  const [authCode, setAuthCode] = useState('');
  const defaultRemainingTime = 180;
  // 알림톡은 통상 1~2초 내 도착한다. 이보다 충분히 여유를 두되,
  // 3분 만료까지 기다리게 하지 않는 지점에서 미수신 안내를 띄운다.
  const hintAfterSeconds = 15;
  const [remainingTime, setRemainingTime] = useState(defaultRemainingTime);
  const [hideAuthCodeInput, setHideAuthCodeInput] = useState(true);
  const [isExpired, setIsExpired] = useState(false);
  const timerRef = useRef(null);

  // 언마운트 시 타이머 정리 (기존에는 정리되지 않아 unmount 후 setState가 발생했다)
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  // 발송 성공 직후 인증코드 칸으로 커서 이동.
  // 인증코드 input은 requestedAuthCode가 true가 되어야 disabled가 풀리므로
  // 상태 반영이 끝난 뒤(=이 effect)에 focus해야 한다.
  useEffect(() => {
    if (requestedAuthCode) setFocus('authCode');
  }, [requestedAuthCode, setFocus]);

  const elapsedSeconds = defaultRemainingTime - remainingTime;
  const showHint = requestedAuthCode && elapsedSeconds >= hintAfterSeconds;

  const phone = useMemo(() => {
    return `${watch('phone1')}${watch('phone2')}${watch('phone3')}`;
  }, [watch('phone1'), watch('phone2'), watch('phone3')]);
  const authCodeValue = useMemo(() => {
    return watch('authCode');
  }, [watch('authCode')]);
  // 인증번호 전송 버튼과 엔터 키가 같은 조건을 쓰도록 한 곳에서 판단한다
  const isRequestDisabled =
    watch('phone2')?.length !== 4 || watch('phone3')?.length !== 4 || validPhoneNumber(phone) || requestedAuthCode;
  const requestAuthCode = async () => {
    try {
      const params = {
        phone,
        //phone: `${watch('phone1')}${watch('phone2')}${watch('phone3')}`,
      };
      console.log(params);
      // code 요청
      const response = await GET({
        url: '/auth/getVerificationCode',
        params,
        header: {
          EJE_API_KEY: 'auth',
        },
      });
      const { success, message } = response;
      if (!success) {
        swal({
          title: '인증번호 요청 실패',
          text: message,
          icon: 'error',
        });
        return false;
      }
      setHideAuthCodeInput(false);
      setRequestedAuthCode(true);
      setIsExpired(false);
      setRemainingTime(defaultRemainingTime);

      if (timerRef.current) clearInterval(timerRef.current);
      let time = defaultRemainingTime;
      timerRef.current = setInterval(() => {
        if (time === 1) {
          clearInterval(timerRef.current);
          timerRef.current = null;
          setRequestedAuthCode(false);
          setRemainingTime(defaultRemainingTime);
          setIsExpired(true);
          setValue('authCode', '');
        } else {
          setRemainingTime(--time);
        }
      }, 1000);
    } catch (error) {
      console.error(error);
    }
  };
  // 전화번호 입력 칸의 키 처리
  // - 엔터: 로그인 제출이 아니라 인증번호 전송으로 연결
  // - 백스페이스: 현재 칸이 비어 있으면 이전 칸으로 이동하며 끝 숫자를 지움
  //   (phone1은 '010' 고정이라 이동 대상에서 제외)
  const prevPhoneField = { phone3: 'phone2' };
  const handlePhoneKeyDown = (field) => (event) => {
    if (event.key === 'Backspace') {
      const prevField = prevPhoneField[field];
      if (!prevField || event.target.value) return;
      event.preventDefault();
      const prevValue = watch(prevField) || '';
      setValue(prevField, prevValue.slice(0, -1), { shouldValidate: true });
      setFocus(prevField);
      return;
    }
    if (event.key !== 'Enter') return;
    event.preventDefault();
    if (isRequestDisabled) return;
    requestAuthCode();
  };
  const onSubmit = async (values) => {
    const data = {
      phone,
      authCode: authCodeValue,
    };

    try {
      const response = await POST({
        url: '/auth/tutor/login',
        body: data,
        header: {
          EJE_API_KEY: 'auth',
        },
      });
      const { success, message, token, info } = response;
      if (!success) {
        swal({
          title: '로그인 실패',
          text: message,
          icon: 'error',
        });
        return false;
      }

      // token을 localStorage에 저장
      localStorage.setItem('eje_tutor_token', token);
      setTutorInfo(info);
      setIsVerified(true);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Modal
      open={true}
      onClose={() => {}}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <FadeContainer in={true}>
        <FormWrapper>
          <Header>전화번호 인증</Header>
          <Description>전화번호 인증을 진행해주세요.</Description>
          <Form onSubmit={handleSubmit(onSubmit)}>
            {/* 전화번호 입력, 전화번호는 010-1234-5678 형태이고 입력필드는 2개 010은 고정으로 한다. */}
            <PhoneFieldWrapper>
              <InputField
                autoComplete="off"
                maxLength={3}
                readOnly
                {...register('phone1', {
                  required: true,
                  maxLength: 3,
                  pattern: /[0-9]/g,
                  readOnly: true,
                })}
              />
              <InputField
                autoComplete="off"
                maxLength={4}
                {...register('phone2', {
                  required: true,
                  maxLength: 4,
                  pattern: /[0-9]/g,
                  validate: (value) => {
                    // pattern match
                    const validPattern = /[0-9]/g;
                    const isValid = validPattern.test(value) && value.length === 4;
                    isValid && setFocus('phone3');
                    return isValid;
                  },
                })}
                className={errors.phone2 ? 'error' : ''}
                onKeyDown={handlePhoneKeyDown('phone2')}
              />
              <InputField
                autoComplete="off"
                maxLength={4}
                {...register('phone3', {
                  required: true,
                  maxLength: 4,
                  pattern: /[0-9]/g,
                })}
                onKeyDown={handlePhoneKeyDown('phone3')}
              />
              <Button
                size="small"
                variant="contained"
                disabled={isRequestDisabled}
                color="info"
                style={{ textWrap: 'nowrap', minWidth: '120px' }}
                sx={{
                  // 전화번호 입력칸(InputField)과 같은 높이로 맞춘다
                  height: '2rem',
                  minHeight: '2rem',
                  padding: '0 0.5rem',
                  boxSizing: 'border-box',
                  // 카운트다운 중에는 disabled라 기본 회색으로 죽는다. 크기는 그대로 두고 색과 굵기만 되살린다.
                  ...(requestedAuthCode && {
                    '&.Mui-disabled': {
                      color: remainingTime <= 30 ? '#eb5757' : '#2f80ed',
                      backgroundColor: remainingTime <= 30 ? '#fdecec' : '#e8f1fd',
                      fontWeight: 700,
                      fontVariantNumeric: 'tabular-nums',
                    },
                  }),
                }}
                onClick={requestAuthCode}
              >
                {requestedAuthCode ? remainingTime.formatVideoTime() : hideAuthCodeInput ? '인증번호 받기' : '재전송'}
              </Button>
            </PhoneFieldWrapper>
            <PhoneFieldWrapper props={{ hideAuthCodeInput }}>
              <InputField
                type="text"
                // 이 행은 hideAuthCodeInput이 true면 아예 숨겨지므로, 문구는 유효시간 기준으로 갈라야 한다
                placeholder={requestedAuthCode ? '인증코드를 입력해주세요.' : '인증코드를 다시 받아주세요.'}
                disabled={!requestedAuthCode}
                {...register('authCode', {
                  required: true,
                  maxLength: 6,
                })}
              />
              <Button
                variant="contained"
                size="small"
                type="submit"
                disabled={!requestedAuthCode || !watch('authCode')}
                style={{ textWrap: 'nowrap', minWidth: '120px' }}
                sx={{
                  // 위 행 버튼·입력칸과 높이를 통일한다
                  height: '2rem',
                  minHeight: '2rem',
                  padding: '0 0.5rem',
                  boxSizing: 'border-box',
                }}
              >
                인증 및 로그인
              </Button>
            </PhoneFieldWrapper>
            {showHint && (
              <HintBox>
                <HintTitle>인증번호가 도착하지 않았나요?</HintTitle>
                카카오톡을 먼저 확인해 주세요. 그래도 오지 않으면 남은 시간이 끝난 뒤 재전송하거나 관리자에게 문의해
                주세요.
              </HintBox>
            )}
            {isExpired && (
              <HintBox $strong>
                <HintTitle>인증번호가 만료되었습니다.</HintTitle>
                재전송을 눌러 다시 받아 주세요. 계속 수신되지 않으면 관리자에게 문의해 주세요.
              </HintBox>
            )}
          </Form>
        </FormWrapper>
      </FadeContainer>
    </Modal>
  );
};

export default LoginDialog;
